import type React from "react";
import { AlertTriangle } from "lucide-react";
import type { insights } from "@duress/shared";
import { cn } from "@/lib/utils";
import { ALL_METRIC_COLUMNS, type MetricColumn } from "./insights-config";
import { useBenchmark } from "./useInsights";

// Below this many peers, a rank/average carries little statistical weight —
// say so in the header rather than presenting it with false confidence.
const LOW_PEER_COUNT_THRESHOLD = 5;

// Gaps smaller than this (as a fraction of the peer average) read as "level"
// rather than a false win/loss on essentially-tied numbers.
const DEAD_BAND = 0.02;

interface GradedMetric {
  key: string;
  label: string;
  short: string;
  sub?: string;
  lowerIsBetter: boolean;
  format: (v: number) => string;
  stats: insights.PeerComparison | null;
}

function findColumn(key: insights.MetricKey) {
  const col = ALL_METRIC_COLUMNS.find((c) => c.key === key);
  if (!col) throw new Error(`Missing insights column config for ${key}`);
  return col;
}

export function IndustryBenchmark({ clientId }: { clientId: string }): React.JSX.Element | null {
  const { data, isLoading, error } = useBenchmark(clientId, null);

  // Fail silently per spec — don't clutter the page if the fetch errors.
  if (isLoading || error || !data) return null;

  if (data.industries.length === 0) {
    return (
      <section className="card-duress p-5">
        <h2 className="font-head font-semibold text-sm">vs Industry Peers</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Add an industry tag to this client to see how they compare to peers.
        </p>
      </section>
    );
  }

  if (data.peerCount === 0) {
    return (
      <section className="card-duress p-5">
        <h2 className="font-head font-semibold text-sm">vs Industry Peers</h2>
        <p className="mt-1 text-xs section-label">
          {data.industries.join(", ")} · no other clients tagged yet
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          No peers to compare against. Tag more clients with the same industry to see benchmarks here.
        </p>
      </section>
    );
  }

  // Rate-shaped metrics — size-neutral, so a bigger and a smaller client can
  // be graded against each other directly.
  const gradedMetrics: GradedMetric[] = [
    { ...findColumn("network_adoption"), stats: data.metrics.network_adoption },
    { ...findColumn("extended_rate"), stats: data.metrics.extended_rate },
    {
      key: "emergency_rate",
      label: "Emergency activations",
      short: "emergency rate",
      sub: "per 1,000 sessions",
      lowerIsBetter: true,
      format: (v: number) => v.toFixed(1),
      stats: data.metrics.emergency_rate,
    },
    { ...findColumn("checkin_rate"), stats: data.metrics.checkin_rate },
  ].filter((m) => m.stats !== null);

  const verdicts = gradedMetrics.map((m) => ({
    metric: m,
    verdict: m.stats ? classify(m.stats, m.lowerIsBetter) : null,
  }));
  const headline = buildHeadline(verdicts);

  const rawScaleContext: { col: MetricColumn; stats: insights.PeerComparison | null }[] = [
    { col: findColumn("sessions"), stats: data.metrics.sessions },
    { col: findColumn("productive_hours"), stats: data.metrics.productive_hours },
  ];
  const scaleContext = rawScaleContext.filter(
    (s): s is { col: MetricColumn; stats: insights.PeerComparison } => s.stats !== null,
  );

  return (
    <section className="card-duress p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-head font-semibold text-sm">vs Industry Peers</h2>
        {data.peerCount < LOW_PEER_COUNT_THRESHOLD ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-pill-orange">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {data.industries.join(", ")} · {data.peerCount}{" "}
            {data.peerCount === 1 ? "peer" : "peers"} — directional only
          </p>
        ) : (
          <p className="section-label">
            {data.industries.join(", ")} · {data.peerCount} peers
          </p>
        )}
      </div>

      {headline ? <p className="mt-2 text-sm text-foreground">{headline}</p> : null}

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_56px_minmax(120px,1fr)_72px] items-center gap-x-3 gap-y-3 sm:gap-x-4">
        <div />
        <div className="text-right text-[11px] uppercase tracking-[0.7px] text-muted-foreground">
          Client
        </div>
        <div className="flex justify-between text-[11px] uppercase tracking-[0.7px] text-muted-foreground">
          <span>worse</span>
          <span>better</span>
        </div>
        <div />

        {gradedMetrics.map((m) =>
          m.stats ? (
            <BenchmarkRow
              key={m.key}
              label={m.label}
              sub={m.sub}
              lowerIsBetter={m.lowerIsBetter}
              stats={m.stats}
              format={m.format}
            />
          ) : null,
        )}
      </div>

      {scaleContext.length > 0 ? (
        <div className="mt-4 border-t border-border pt-3">
          <p className="section-label">Scale, for context — not scored</p>
          <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            {scaleContext.map(({ col, stats }) => (
              <ScaleContextItem key={col.key} col={col} stats={stats} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

type Verdict = "better" | "worse" | "level";

function classify(stats: insights.PeerComparison, lowerIsBetter: boolean): Verdict | null {
  if (stats.client === null) return null;
  const raw = pctDiff(stats.client, stats.peerAvg);
  if (raw === null) return stats.client === stats.peerAvg ? "level" : lowerIsBetter ? "worse" : "better";
  const directional = lowerIsBetter ? -raw : raw;
  if (Math.abs(directional) < DEAD_BAND) return "level";
  return directional > 0 ? "better" : "worse";
}

// Difference as a fraction of the peer average. Null when the average is
// zero and the two values differ, since there's no meaningful "% of zero".
function pctDiff(client: number, peerAvg: number): number | null {
  if (peerAvg === 0) return client === 0 ? 0 : null;
  return (client - peerAvg) / peerAvg;
}

function buildHeadline(
  verdicts: { metric: GradedMetric; verdict: Verdict | null }[],
): string | null {
  const better = verdicts.filter((v) => v.verdict === "better").map((v) => v.metric.short);
  const worse = verdicts.filter((v) => v.verdict === "worse").map((v) => v.metric.short);
  if (better.length === 0 && worse.length === 0) {
    return verdicts.some((v) => v.verdict !== null)
      ? "In line with peers across the metrics tracked this period."
      : null;
  }
  const parts: string[] = [];
  if (better.length > 0) parts.push(`Ahead of peers on ${joinWithAnd(better)}`);
  if (worse.length > 0) parts.push(`behind on ${joinWithAnd(worse)}`);
  return `${parts.join(", ")}.`;
}

function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  const last = items[items.length - 1] ?? "";
  if (items.length === 2) return `${items[0] ?? ""} and ${last}`;
  return `${items.slice(0, -1).join(", ")} and ${last}`;
}

function BenchmarkRow({
  label,
  sub,
  lowerIsBetter,
  stats,
  format,
}: {
  label: string;
  sub: string | undefined;
  lowerIsBetter: boolean;
  stats: insights.PeerComparison;
  format: (v: number) => string;
}): React.JSX.Element {
  const clientValue = stats.client;
  const verdict = classify(stats, lowerIsBetter);

  // Domain is centred on the peer average and sized to fit the client value
  // and the peer min/max, so the dot never clamps to an edge and loses
  // meaning. A small floor keeps the axis sane when everything is ~equal.
  const deviations = [
    Math.abs(stats.peerMax - stats.peerAvg),
    Math.abs(stats.peerMin - stats.peerAvg),
    clientValue !== null ? Math.abs(clientValue - stats.peerAvg) : 0,
  ];
  const radius = Math.max(...deviations, Math.abs(stats.peerAvg) * 0.1 || 1) * 1.15;

  const clientPct =
    clientValue !== null
      ? clamp(
          50 + (lowerIsBetter ? -1 : 1) * (((clientValue - stats.peerAvg) / radius) * 50),
          2,
          98,
        )
      : null;

  const gapLabel = gapLabelFor(stats, verdict);

  return (
    <>
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">
          peers {format(stats.peerAvg)}
          {sub ? ` · ${sub}` : null}
          {lowerIsBetter ? " · lower is better" : null}
        </div>
      </div>

      <div className="num text-right text-sm font-semibold tabular-nums text-foreground">
        {clientValue !== null ? format(clientValue) : "—"}
      </div>

      <div
        className="relative flex h-[9px] overflow-hidden rounded-full"
        role="img"
        aria-label={`${label}: ${clientValue !== null ? format(clientValue) : "not reported"}, peer average ${format(stats.peerAvg)}`}
      >
        <div className="w-1/2 bg-emergency-red/15" />
        <div className="w-1/2 bg-falcon-green/15" />
        <div
          className="absolute top-0 bottom-0 left-1/2 w-px bg-muted-foreground/50"
          aria-hidden
        />
        {clientPct !== null ? (
          <div
            className={cn(
              "absolute top-0 bottom-0",
              clientPct >= 50 ? "left-1/2" : "right-1/2",
              verdict === "better"
                ? "bg-falcon-green"
                : verdict === "worse"
                  ? "bg-emergency-red"
                  : "bg-muted-foreground",
            )}
            style={{ width: `${String(Math.abs(clientPct - 50))}%` }}
          />
        ) : null}
      </div>

      <div className="text-right">
        {gapLabel ? (
          <span
            className={cn(
              "num inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
              verdict === "better"
                ? "text-pill-green bg-falcon-green/10"
                : verdict === "worse"
                  ? "text-pill-red bg-emergency-red/10"
                  : "text-muted-foreground bg-muted",
            )}
          >
            {gapLabel}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">not reported</span>
        )}
      </div>
    </>
  );
}

function gapLabelFor(stats: insights.PeerComparison, verdict: Verdict | null): string | null {
  if (stats.client === null || verdict === null) return null;
  if (verdict === "level") return "level";
  const raw = pctDiff(stats.client, stats.peerAvg);
  if (raw === null) return verdict === "worse" ? "worse" : "better";
  const pct = String(Math.round(Math.abs(raw) * 100));
  return `${pct}% ${verdict}`;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function ScaleContextItem({
  col,
  stats,
}: {
  col: MetricColumn;
  stats: insights.PeerComparison;
}): React.JSX.Element | null {
  const { label, format } = col;
  if (stats.client === null) return null;
  const multiple = stats.peerAvg !== 0 ? stats.client / stats.peerAvg : null;
  return (
    <span>
      {label}{" "}
      <span className="num font-semibold text-foreground tabular-nums">
        {format(stats.client)}
      </span>
      {multiple !== null ? (
        <>
          {" "}
          ·{" "}
          <span className="num tabular-nums">
            {multiple >= 10 ? Math.round(multiple) : multiple.toFixed(1)}×
          </span>{" "}
          the peer average
        </>
      ) : null}
    </span>
  );
}
